/**
 * Kleuren rekenen voor het kleurenrad van de huisstijl (BHR-8).
 *
 * Het rad werkt in HSL: de hoek is de tint, de afstand tot het midden de verzadiging, en de schuif
 * eronder de helderheid (zwart → de kleur → wit). Een hexcode is wat er bewaard wordt; HSL is
 * alleen hoe je erheen draait.
 *
 * **Het contrast is een kopie, geen tweede regel.** De server rekent de varianten die de app echt
 * gebruikt (`apps/api/.../domain/contrast.ts`, ADR-0056). Het scherm moet ze tonen vóór er iets
 * bewaard is - "het voorbeeld past meteen aan" - en daarvoor rekent het hier hetzelfde uit, met
 * dezelfde stappen. Na opslaan geldt wat de server terugstuurt.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/** Tint 0–360, verzadiging en helderheid 0–100. */
export interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** `#rrggbb` of `#rgb`, met of zonder hekje. Al het andere is geen kleur. */
export function parseHex(value: string): Rgb | null {
  const hex = value.trim().replace(/^#/, '').toLowerCase();
  if (!/^[0-9a-f]{3}$|^[0-9a-f]{6}$/.test(hex)) return null;
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex;
  return {
    r: Number.parseInt(full.slice(0, 2), 16),
    g: Number.parseInt(full.slice(2, 4), 16),
    b: Number.parseInt(full.slice(4, 6), 16),
  };
}

/** Een hexcode in hoofdletters, zoals het scherm hem toont: `#F05A28`. */
export function toHex({ r, g, b }: Rgb): string {
  const part = (value: number) =>
    Math.max(0, Math.min(255, Math.round(value)))
      .toString(16)
      .padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`.toUpperCase();
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return { h: 0, s: 0, l: l * 100 };
  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (max === red) h = ((green - blue) / d) % 6;
  else if (max === green) h = (blue - red) / d + 2;
  else h = (red - green) / d + 4;
  h *= 60;
  if (h < 0) h += 360;
  return { h, s: s * 100, l: l * 100 };
}

export function hslToRgb({ h, s, l }: Hsl): Rgb {
  const sat = Math.max(0, Math.min(100, s)) / 100;
  const light = Math.max(0, Math.min(100, l)) / 100;
  const c = (1 - Math.abs(2 * light - 1)) * sat;
  const hue = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hue % 2) - 1));
  const [r1, g1, b1] =
    hue < 1
      ? [c, x, 0]
      : hue < 2
        ? [x, c, 0]
        : hue < 3
          ? [0, c, x]
          : hue < 4
            ? [0, x, c]
            : hue < 5
              ? [x, 0, c]
              : [c, 0, x];
  const m = light - c / 2;
  return { r: (r1 + m) * 255, g: (g1 + m) * 255, b: (b1 + m) * 255 };
}

export function hslToHex(hsl: Hsl): string {
  return toHex(hslToRgb(hsl));
}

export function hexToHsl(value: string): Hsl | null {
  const rgb = parseHex(value);
  return rgb ? rgbToHsl(rgb) : null;
}

/** Relatieve luminantie volgens WCAG 2.1. */
function luminance({ r, g, b }: Rgb): number {
  const channel = (raw: number) => {
    const value = raw / 255;
    return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrastRatio(a: Rgb, b: Rgb): number {
  const left = luminance(a);
  const right = luminance(b);
  return (Math.max(left, right) + 0.05) / (Math.min(left, right) + 0.05);
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
const INK = '#1A1D2E';

/** De drempel voor tekst en bediening (WCAG AA), en die voor een rand (1.4.11). */
export const AA_CONTRAST = 4.5;
const AA_LARGE_CONTRAST = 3;

/** Zoals de server: twintig stappen van vijf procent donkerder, tot wit erop leesbaar is. */
function darkenUntilReadable(color: Rgb, minimum: number): Rgb {
  let current = { ...color };
  for (let step = 0; step < 20; step += 1) {
    if (contrastRatio(current, WHITE) >= minimum) return current;
    current = { r: current.r * 0.95, g: current.g * 0.95, b: current.b * 0.95 };
  }
  return { r: 0, g: 0, b: 0 };
}

/** Wit of inkt: wat het hoogste contrast geeft op dit vlak. */
function textColorOn(background: Rgb): string {
  const ink = parseHex(INK) as Rgb;
  return contrastRatio(background, WHITE) >= contrastRatio(background, ink) ? '#FFFFFF' : INK;
}

/** Wat het voorbeeld van één kleur nodig heeft: dezelfde varianten als de server rekent. */
export interface ColourCheck {
  /** De kleur zelf, voor een vlak. */
  hex: string;
  /** Het contrast van witte tekst op de kleur. */
  whiteRatio: number;
  /** De tint voor knoppen en links: de kleur zelf, of donkerder tot 4,5 : 1. */
  readable: string;
  readableRatio: number;
  /** Wit of inkt bovenop de kleur. */
  on: string;
  /** De randvariant (3 : 1), voor de accentkleur. */
  edge: string;
}

export function checkColour(hex: string): ColourCheck | null {
  const rgb = parseHex(hex);
  if (!rgb) return null;
  const readable = darkenUntilReadable(rgb, AA_CONTRAST);
  return {
    hex: toHex(rgb),
    whiteRatio: contrastRatio(rgb, WHITE),
    readable: toHex(readable),
    readableRatio: contrastRatio(readable, WHITE),
    on: textColorOn(rgb),
    edge: toHex(darkenUntilReadable(rgb, AA_LARGE_CONTRAST)),
  };
}

/**
 * Wat er in het hexveld staat, plus waar het rad staat.
 *
 * Twee waarden en niet één: wie het rad naar wit sleept, verliest in een hexcode zijn tint
 * (#FFFFFF heeft er geen). Het rad houdt hem vast, zodat terugdraaien weer bij dezelfde kleur
 * uitkomt.
 */
export interface ColourState {
  text: string;
  hsl: Hsl;
}

export function colourState(text: string, fallback: string): ColourState {
  return {
    text,
    hsl: hexToHsl(text) ?? hexToHsl(fallback) ?? { h: 194, s: 100, l: 43 },
  };
}

/** De kleur die het scherm toont: de getypte code als die klopt, anders waar het rad staat. */
export function effectiveHex(state: ColourState): string {
  const rgb = parseHex(state.text);
  return rgb ? toHex(rgb) : hslToHex(state.hsl);
}

/** De plek op het rad (in procent van de breedte), van tint en verzadiging. */
export function wheelPosition({ h, s }: Hsl): { left: number; top: number } {
  const angle = (h * Math.PI) / 180;
  const radius = (Math.max(0, Math.min(100, s)) / 100) * 50;
  return { left: 50 + radius * Math.sin(angle), top: 50 - radius * Math.cos(angle) };
}

/** Omgekeerd: een punt op het rad (0–1 van links en van boven) naar tint en verzadiging. */
export function wheelPick(x: number, y: number): { h: number; s: number } {
  const dx = x - 0.5;
  const dy = y - 0.5;
  // De `conic-gradient` begint bovenaan en draait met de klok mee: rood boven, geel rechtsboven.
  let h = (Math.atan2(dx, -dy) * 180) / Math.PI;
  if (h < 0) h += 360;
  const s = Math.min(1, Math.sqrt(dx * dx + dy * dy) / 0.5) * 100;
  return { h, s };
}
